import styled from 'styled-components';
import moment from 'moment-timezone';

import { Trans } from 'services/i18n';
import { Tooltip } from 'ui/tooltip';
import { Avatar, AVATAR_SIZES } from 'ui/avatar';
import {
  displayDateWithTodayAndYesterdayPrefix,
  displayDate,
  DATE_FORMAT_WITH_COMMA_HOUR_MINS,
} from 'utils/date';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';

import { Cell } from './table';
import { TABLE_COLUMNS } from './constants';
import { useTranslation } from '../../hooks';
import { ListReference, ModifierAndCreatorById, User } from '../../domain';

type ListReferenceLastModifiedCellProps = {
  listReference: ListReference;
  rowIndex: number;
  modifiersAndCreatorsById: ModifierAndCreatorById;
};

const LastModifiedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ModifiedDateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
`;

const ModifiedAgoWrapper = styled.div`
  ${({ theme }) => theme.styles.captions.caption1}
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
`;

const DateTooltipContent = ({ date, name }: { date: string | number; name: string }) => {
  const { t } = useTranslation();
  return (
    <>
      {displayDate(date, DATE_FORMAT_WITH_COMMA_HOUR_MINS)}
      <br />
      {t('insights.listUsage.createdBy', { name })}
    </>
  );
};

const AvatarTooltipContent = ({ email, name }: { email: string; name?: string }) => (
  <div>
    {name && <div>{name}</div>}
    <div>{email}</div>
  </div>
);

const getUpdatedBy = (dependent: ListReference) => {
  if (dependent.__typename === GRAPHQL_TYPENAMES.LIST) {
    return dependent.creatorUserId;
  } else if (dependent.__typename === GRAPHQL_TYPENAMES.CAMPAIGN) {
    return dependent.updatedBy || dependent.createdBy;
  } else {
    return dependent.updatedByUserId || dependent.creatorUserId;
  }
};

const getUserInfo = (userInfo: User | null, defaultId: string): User => {
  if (userInfo) {
    const { id, fullName, avatarUrl } = userInfo;
    return {
      id,
      fullName,
      avatarUrl,
    };
  } else {
    return {
      id: defaultId,
      fullName: '',
    };
  }
};

export const ListReferenceLastModifiedCell = ({
  listReference: dependent,
  rowIndex,
  modifiersAndCreatorsById,
}: ListReferenceLastModifiedCellProps) => {
  const updatedByUserId = getUpdatedBy(dependent);
  const { t } = useTranslation();
  const {
    id: avatarEmail,
    fullName: avatarName,
    avatarUrl,
  } = getUserInfo(modifiersAndCreatorsById[updatedByUserId], updatedByUserId);

  const modifiedDate = displayDateWithTodayAndYesterdayPrefix(
    dependent.updatedAt || dependent.createdAt,
  );
  const modifiedAgo = moment(dependent.updatedAt || dependent.createdAt).fromNow();

  return (
    <Cell
      columnId={TABLE_COLUMNS.LAST_MODIFIED}
      rowIndex={rowIndex}
      data-test="list-usage-modified"
    >
      <LastModifiedWrapper>
        <Tooltip
          isEnabled={!!avatarName || !!avatarEmail}
          tooltipContent={<AvatarTooltipContent email={avatarEmail} name={avatarName} />}
        >
          <Avatar size={AVATAR_SIZES.MEDIUM} firstName={avatarName} imgSrc={avatarUrl} />
        </Tooltip>
        <ModifiedDateWrapper>
          {modifiedDate}
          <ModifiedAgoWrapper>
            <Trans
              t={t}
              i18nKey="insights.listUsage.createdAt"
              values={{ modifiedAgo }}
              components={{
                Tooltip: (
                  <Tooltip
                    isUnderlined
                    tooltipContent={
                      <DateTooltipContent date={dependent.updatedAt} name={updatedByUserId} />
                    }
                  >
                    <>{{ modifiedAgo }}</>
                  </Tooltip>
                ),
              }}
            />
          </ModifiedAgoWrapper>
        </ModifiedDateWrapper>
      </LastModifiedWrapper>
    </Cell>
  );
};
